import {
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Grid, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate, useParams } from "react-router-dom";
import RoundedContentBox from "@/components/general/roundedContentBox";
import ImageViewer from "@/components/general/imageViewer";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import LmsCourseOverviewHeader from "@/components/lms/lmsCourseOverviewHeader";
import LmsBackButton from "@/components/lms/lmsBackButton";
import LmsCategoryDepiction from "@/components/lms/lmsCategoryDepiction";
import LmsEnrolmentLogsView from "@/components/lms/lmsEnrolmentLogsView";
import {
  getAvailableDurationsLabel,
  getAvailableLanguagesLabel,
  getLearnerStatusPresentation,
} from "@/components/lms/lmsCoursesShared";
import { LmsStatusPill } from "@/components/lms/lmsStatusChip";
import { RootState } from "@/components/state/store";
import { getImageUrl } from "@/helper/imageUrlHandler";
import {
  createPromiseRequest,
  downloadLmsCertificate,
  formatDateValue,
  getEffectiveLmsMode,
  getLmsCourseFeaturedImage,
  getLmsCourseSelectedLanguage,
  getLmsCoursesRoute,
  getLmsManagerRequestConfig,
  getLmsManagerRequestParameters,
  getLmsScormLaunchRoute,
  LmsMode,
  shouldBlockLmsForSubdomain,
} from "@/components/lms/lms.utils";
import headerStyles from "@/routes/lms/lmsCourseOverview.module.css";
import styles from "@/routes/lms/lmsCourseOverviewReadOnly.module.css";

type SummaryItem = {
  key: string;
  label: string;
  value: ReactNode;
  infoTooltip?: string;
};

type OverviewTab = "overview" | "history";

interface Props {
  requiredMode?: LmsMode;
  subdomain: "portal" | "manage";
  renderOnlyOverview?: boolean;
  onCourseLoaded?: (course: any) => void;
  courseData?: any;
  isCourseLoading?: boolean;
}

type LearnerCta =
  | { hidden: true }
  | { hidden: false; label: string; icon: "play" | "review" };

const normalizeLearnerStatus = (status?: string | null) =>
  (status ?? "").trim().toLowerCase();

const getCtaForStatus = (
  t: (k: string) => string,
  status?: string | null
): LearnerCta => {
  const normalized = normalizeLearnerStatus(status);

  if (normalized === "completed") {
    return { hidden: false, label: t("review_course"), icon: "review" };
  }

  if (normalized === "in_progress") {
    return { hidden: false, label: t("continue_course"), icon: "play" };
  }

  if (normalized === "not_started" || normalized === "not_started_yet") {
    return { hidden: false, label: t("start_course"), icon: "play" };
  }

  return { hidden: false, label: t("start_course"), icon: "play" };
};

export default function LmsCourseOverviewReadOnly({
  requiredMode,
  subdomain: readOnlySubdomain,
  renderOnlyOverview = false,
  onCourseLoaded,
  courseData,
  isCourseLoading,
}: Props) {
  const { t } = useTranslation();
  const loginState = useSelector((state: RootState) => state.login);
  const selectedChildCompany = useSelector((state: RootState) => state.parent);
  const { courseId, enrolmentId } = useParams<{
    courseId?: string;
    enrolmentId?: string;
  }>();
  const [activeTab, setActiveTab] = useState<OverviewTab>("overview");
  const [isLoading, setIsLoading] = useState(true);
  const { handleError, errorModal } = useErrorModal();
  const [enrolment, setEnrolment] = useState<any>(null);
  const [course, setCourse] = useState<any>(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [visibilityRefreshVersion, setVisibilityRefreshVersion] = useState(0);
  const usesProvidedCourse = courseData !== undefined;
  const resolvedCourse = usesProvidedCourse ? courseData : course;
  const resolvedIsLoading = isCourseLoading ?? isLoading;

  const subdomain = window.location.hostname.split(".")[0];
  const shouldBlockLms = shouldBlockLmsForSubdomain({
    subdomain,
    permissions: loginState.permissions,
  });
  const effectiveMode = getEffectiveLmsMode({
    subdomain,
    permissions: loginState.permissions,
  });
  const courseResourceId = resolvedCourse?.id
    ? String(resolvedCourse.id)
    : courseId
    ? String(courseId)
    : "";
  const selectedLanguage = getLmsCourseSelectedLanguage(resolvedCourse);

  useEffect(() => {
    if (readOnlySubdomain !== "portal" || usesProvidedCourse) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setVisibilityRefreshVersion((currentVersion) => currentVersion + 1);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [readOnlySubdomain, usesProvidedCourse]);

  useEffect(() => {
    if (usesProvidedCourse) return;
    if (!courseId && !enrolmentId) return;

    const load = async () => {
      setIsLoading(true);
      handleError(null);

      try {
        if (readOnlySubdomain === "portal" && enrolmentId) {
          const enrolmentResponse = await createPromiseRequest({
            entity: "enrolment",
            entityId: enrolmentId,
            method: "get",
            parametersToRender: {
              depth: 1,
              prefer_translation: "true",
            },
          });
          const enrolmentResult = enrolmentResponse?.data ?? null;
          const enrolmentCourse = enrolmentResult?.course;
          const courseData =
            enrolmentCourse && typeof enrolmentCourse === "object"
              ? enrolmentCourse
              : null;

          setEnrolment(enrolmentResult);
          setCourse(courseData);
          onCourseLoaded?.(courseData);
          return;
        }

        const enrolmentRequest =
          readOnlySubdomain === "portal"
            ? createPromiseRequest({
                entity: "enrolment",
                method: "get",
                parametersToRender: {
                  participant_id: loginState.subjectId,
                  course_id: courseId,
                  limit: 1,
                },
              })
            : Promise.resolve(null);

        const [enrolmentResponse, courseResponse] = await Promise.all([
          enrolmentRequest,
          createPromiseRequest({
            ...getLmsManagerRequestConfig(
              effectiveMode,
              selectedChildCompany.subjectId
            ),
            entity: "course",
            entityId: courseId,
            method: "get",
            parametersToRender: {
              ...getLmsManagerRequestParameters(
                effectiveMode,
                selectedChildCompany.subjectId
              ),
              prefer_translation: "true",
            },
          }),
        ]);

        const enrolmentResult = enrolmentResponse?.data?.results?.[0] ?? null;
        const courseData = courseResponse?.data ?? null;

        setEnrolment(enrolmentResult);
        setCourse(courseData);
        onCourseLoaded?.(courseData);
      } catch (requestError) {
        handleError(requestError);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [
    courseId,
    enrolmentId,
    effectiveMode,
    loginState.subjectId,
    onCourseLoaded,
    readOnlySubdomain,
    selectedChildCompany.subjectId,
    usesProvidedCourse,
    visibilityRefreshVersion,
  ]);

  if (shouldBlockLms) {
    return <Navigate to="/" replace />;
  }

  if (requiredMode && effectiveMode !== requiredMode) {
    return <Navigate to={getLmsCoursesRoute(effectiveMode)} replace />;
  }

  const learnerStatus = enrolment?.status ?? null;
  const cta =
    readOnlySubdomain === "portal"
      ? getCtaForStatus(t, learnerStatus)
      : { hidden: true as const };

  const summaryItems = useMemo((): SummaryItem[] => {
    const languageSummary: SummaryItem = {
      key: "language",
      label: t("available_language"),
      value: getAvailableLanguagesLabel(
        resolvedCourse?.available_languages,
        t,
        selectedLanguage
      ),
      infoTooltip:
        readOnlySubdomain === "portal" &&
        (resolvedCourse?.available_languages?.length ?? 0) > 1
          ? t("lms_available_language_portal_hint")
          : undefined,
    };

    if (readOnlySubdomain === "manage") {
      return [
        {
          key: "category",
          label: t("category"),
          value: <LmsCategoryDepiction category={resolvedCourse?.category} />,
        },
        languageSummary,
        {
          key: "duration",
          label: t("duration"),
          value: getAvailableDurationsLabel(resolvedCourse),
        },
      ];
    }

    const statusPresentation = getLearnerStatusPresentation(
      t,
      learnerStatus,
      enrolment?.progress_percentage
    );

    return [
      {
        key: "invited_on",
        label: t("invited_on"),
        value: formatDateValue(enrolment?.created_on),
      },
      {
        key: "status",
        label: t("status"),
        value: statusPresentation.label ? (
          <LmsStatusPill
            tone={statusPresentation.tone}
            label={statusPresentation.label}
          />
        ) : (
          "-"
        ),
      },
      {
        key: "completed_on",
        label: t("completed_on"),
        value: formatDateValue(enrolment?.completed_on),
      },
      languageSummary,
      {
        key: "duration",
        label: t("duration"),
        value: getAvailableDurationsLabel(resolvedCourse),
      },
    ];
  }, [
    resolvedCourse?.category,
    resolvedCourse?.duration_in_minutes,
    resolvedCourse?.available_languages,
    resolvedCourse?.selected_language,
    resolvedCourse?.variants,
    selectedLanguage,
    enrolment?.completed_on,
    enrolment?.created_on,
    enrolment?.progress_percentage,
    learnerStatus,
    readOnlySubdomain,
    t,
  ]);

  const handleTabChange = (_event: SyntheticEvent, newValue: OverviewTab) => {
    setActiveTab(newValue);
  };

  const renderCtaIcon = () => {
    if (cta.hidden) return null;
    if (cta.icon === "play") return <PlayArrowIcon />;
    return <VisibilityOutlinedIcon />;
  };

  const handlePrimaryAction = () => {
    if (!enrolment?.id) {
      return;
    }

    window.open(
      getLmsScormLaunchRoute(String(enrolment.id)),
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleDownloadCertificate = async () => {
    if (!enrolment?.id) {
      return;
    }

    try {
      await downloadLmsCertificate({ enrolmentId: String(enrolment.id) });
    } catch (message) {
      handleError(message);
    }
  };

  const showDownloadCertificate =
    readOnlySubdomain === "portal" &&
    normalizeLearnerStatus(learnerStatus) === "completed" &&
    Boolean(enrolment?.id);

  const featuredImage = getLmsCourseFeaturedImage(resolvedCourse);

  const featuredImageUrl = featuredImage?.viewLink
    ? getImageUrl(
        featuredImage.viewLink,
        readOnlySubdomain === "manage" && selectedChildCompany.subjectId
          ? String(selectedChildCompany.subjectId)
          : undefined
      )
    : null;

  const featuredImageViewerImages = useMemo(
    () =>
      featuredImage?.viewLink && courseResourceId
        ? [
            {
              id: featuredImage.id || courseResourceId,
              name: featuredImage.displayName,
              view_link: featuredImage.viewLink,
              download_link: featuredImage.viewLink,
              extension: featuredImage.displayName.split(".").pop() || "png",
            },
          ]
        : [],
    [courseResourceId, featuredImage]
  );

  const hasHeaderActions = !cta.hidden || showDownloadCertificate;

  return (
    <>
      <Grid container className={styles.pageGrid}>
        {!renderOnlyOverview && (
          <Grid item xs={12}>
            <div className={headerStyles.courseOverviewNav}>
              <LmsBackButton />
            </div>
            <RoundedContentBox>
              <LmsCourseOverviewHeader
                featuredImageUrl={featuredImageUrl}
                imageAlt={
                  featuredImage?.displayName ||
                  resolvedCourse?.title ||
                  resolvedCourse?.name ||
                  t("course_featured_image")
                }
                title={
                  resolvedCourse?.title ?? resolvedCourse?.name ?? t("courses")
                }
                onViewImage={() => setImageViewerOpen(true)}
                canViewImage={Boolean(featuredImage?.viewLink)}
                topRightContent={
                  hasHeaderActions ? (
                    <div className={headerStyles.headerActionsContainer}>
                      {showDownloadCertificate && (
                        <GenericButton
                          variant="outlined"
                          color="tertiary"
                          onClick={handleDownloadCertificate}
                          startIcon={<FileDownloadIcon />}
                          className={headerStyles.headerActionButton}
                          data-testid="lms-learner-certificate-download-button"
                        >
                          {t("lms_download_certificate")}
                        </GenericButton>
                      )}
                      {!cta.hidden && (
                        <GenericButton
                          onClick={handlePrimaryAction}
                          startIcon={renderCtaIcon()}
                          className={headerStyles.headerActionButton}
                          data-testid="lms-learner-course-primary-action-button"
                          data-enrolment-id={enrolment?.id ?? ""}
                        >
                          {cta.label}
                        </GenericButton>
                      )}
                    </div>
                  ) : null
                }
              >
                {readOnlySubdomain === "portal" && (
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    className={headerStyles.tabsContainer}
                    variant="scrollable"
                    scrollButtons={false}
                    allowScrollButtonsMobile
                    sx={{
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#8c1ec8",
                      },
                      "& .MuiTab-root": {
                        minWidth: "auto",
                        flexShrink: 0,
                        fontSize: {
                          xs: "0.75rem",
                          sm: "0.8125rem",
                          md: "0.875rem",
                        },
                        padding: { xs: "0.75rem 0.625rem", sm: "0.75rem 1rem" },
                      },
                      "& .MuiTab-root.Mui-selected": {
                        color: "#8c1ec8",
                      },
                    }}
                  >
                    <Tab
                      value="overview"
                      label={t("overview")}
                      data-testid="lms-learner-course-overview-tab"
                    />
                    <Tab
                      value="history"
                      label={t("history")}
                      data-testid="lms-learner-course-history-tab"
                    />
                  </Tabs>
                )}
              </LmsCourseOverviewHeader>
            </RoundedContentBox>
          </Grid>
        )}

        <Grid item xs={12}>
          {resolvedIsLoading ? (
            <RoundedContentBox>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={6}
              >
                <Typography>{t("loading")}</Typography>
              </Box>
            </RoundedContentBox>
          ) : (
            <div className={styles.contentWrapper}>
              {activeTab === "overview" && (
                <div
                  className={`${styles.summaryGrid} ${
                    readOnlySubdomain === "manage"
                      ? styles.summaryGridManage
                      : ""
                  }`}
                >
                  {summaryItems.map((item) => (
                    <div key={item.key} className={styles.summaryCard}>
                      <div className={styles.summaryLabel}>
                        {item.label}
                        {item.infoTooltip && (
                          <Tooltip title={item.infoTooltip} placement="top" arrow>
                            <span
                              aria-label={item.infoTooltip}
                              className={styles.summaryInfoIcon}
                              data-testid="lms-available-language-info"
                              tabIndex={0}
                            >
                              <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <div className={styles.summaryValue}>{item.value}</div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "overview" ? (
                <div className={styles.contentSection}>
                  <div
                    className={styles.richText}
                    dangerouslySetInnerHTML={{
                      __html: String(resolvedCourse?.description ?? ""),
                    }}
                  />
                </div>
              ) : (
                <div className={styles.contentSection}>
                  <LmsEnrolmentLogsView enrolment={enrolment} />
                </div>
              )}
            </div>
          )}
        </Grid>
      </Grid>

      {featuredImageViewerImages.length > 0 && (
        <ImageViewer
          open={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          images={featuredImageViewerImages}
          initialImageIndex={0}
          doNotShowFileName={subdomain !== "admin"}
        />
      )}

      {errorModal}
    </>
  );
}
